import { datadogRum } from '@datadog/browser-rum';
import { FlagProvider, useUnleashContext } from '@unleash/proxy-client-react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { BrowserRouter, HashRouter, Navigate, Route, Routes } from 'react-router-dom';

import { Zendesk } from '@/lib/components/Zendesk';
import { Spinner } from '@/lib/v2/components';

import UserProvider from './application/context/UserContext';
import { useLanguage } from './application/hooks/useLanguage';
import { useUserData } from './application/hooks/useUserData';
import { useVersion } from './application/hooks/useVersion';
import { Environments, getEnv } from './application/hooks/util/useEnv';
import { useFeatureFlag } from './application/hooks/util/useFeatureFlag';
import { IdentifyUser } from './application/util/mixPanel';
import { sendEvent } from './compat/iframeIntercom';
import { parseJwt, readCookie } from './compat/util';
import { FileProvider } from './ContactsModule/contexts/ImportFileContext';
import { ServiceMethods } from './infrastructure/Protocol/EmblueService';
import { useEmblue, useService } from './infrastructure/Protocol/useEmblue';
import { LoginLayout } from './modules/AuthModule';
import { AUTH_PATHS } from './modules/AuthModule/constants';
import MultiFactorAuthentication from './modules/AuthModule/screens/MultiFactorAuthentication';
import { CreateSenderModal } from './modules/ContactsModule/components';
import { BlockAndSuspendModal } from './presentation/components/BlockAndSuspendModal';
import DownloadContacts from './presentation/components/DownloadContacts';
import { FreeTrialDemo } from './presentation/components/FreeTrialDemo';
import { DashboardContainer } from './presentation/screens/DashBoard';
import MainContainer from './presentation/screens/MainContainer';
import { IFreeTrialDemo } from './presentation/types/interfaces/IFreeTrialDemo';
import { closeDropDown } from './presentation/util/constants/closeDropDown';
import AppRouterVersionTwo from './routers/AppRouterVersionTwo';
import configData from './config.json';

import './index.css';

import OnsiteScreen from '@/modules/OnSiteModule/screens/OnsiteScreen/OnsiteScreen';

export const EmblueLegacyContext = createContext({
  contacts: false,
  setContext: (() => {}) as Function,
});

const onsiteEnabled = (window as any).__onsiteEnabled || false;

const AppInstance = () => {
  const env = getEnv() as Exclude<Environments, 'storybook'>;
  const { JWT_DEV_TOKEN } = configData.endpoints[env];
  const jwtToken = JWT_DEV_TOKEN !== '' ? JWT_DEV_TOKEN : (window as any).__JWTToken;

  const [userData] = useUserData();
  const updateContext = useUnleashContext();
  const { contacts } = useContext(EmblueLegacyContext);
  const { version } = useVersion();
  const showBlockModalFreeTrialDemo = useFeatureFlag('showBlockModalFreeTrialDemo');
  const loginV2CheckCountryIsEnabled = useFeatureFlag('loginV2CheckCountry');
  const MFAIsEnabled = useFeatureFlag('MFA');
  const showStatusCardsFF = useFeatureFlag('showStatusCards');
  const { language } = useLanguage({ userData, version });
  const { i18n, ready } = useTranslation();
  const [paymentInfo] = useEmblue(ServiceMethods.getPaymentInfo);
  const [freeTrial] = useEmblue(ServiceMethods.getFreeTrialBlock);
  const [freeTrialBlockState, setFreeTrialBlockState] = useState<IFreeTrialDemo>({
    isBlocked: false,
    counter: 0,
    showExtendsDemoButton: false,
    showCounter: false,
  });
  const [showSenderCreate, setShowSenderCreate] = useState<boolean>(false);

  const contactsEnabled = useMemo(() => !contacts, [contacts]);

  // const navigate = useNavigate();
  const service = useService();

  let attemptCount = 0;
  const maxAttempts = 10;

  const logoutHandler = () => {
    const intervalId = setInterval(() => {
      sendEvent(configData.HOME.SIGN_OFF, {});

      attemptCount++;
      if (attemptCount >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 1000);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loginV2CheckCountry = async (email: string, accountId: any, contractId: any) => {
    const userMatchWithCountry = await service?.loginV2CheckCountry();
    if (!userMatchWithCountry) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      datadogRum.addAction('userDoesNotMatchWithCountry', { email, accountId, contractId });

      logoutHandler();
    }
  };

  useEffect(() => {
    if (jwtToken) {
      const user = parseJwt(jwtToken);
      (window as any).__emblueMetadata = user;
      (window as any).emblueMetadata = user;
      void updateContext({
        userId: user.contractId,
        properties: {
          userEmail: user.email,
          accountId: user.accountId,
          contractId: user.contractId,
        },
      });

      if (loginV2CheckCountryIsEnabled) {
        void loginV2CheckCountry(user.email, user.accountId, user.contractId);
      }
    }
    if (freeTrial) {
      setFreeTrialBlockState(freeTrial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, updateContext, freeTrial, userData.email, loginV2CheckCountryIsEnabled]);

  useEffect(() => {
    IdentifyUser(userData);
    if (language && i18n && ready) {
      void i18n.changeLanguage(language.toLowerCase());
    }
    datadogRum.init({
      clientToken: 'pub58d3aa31599b679aaf3b81890699ea40',
      applicationId: '5789c698-ac9f-491e-a297-a261e2c131fe',
      site: 'datadoghq.com',
      service: 'ema',
      env: getEnv() === 'production' ? 'prod' : getEnv(),
      version: 'v2.0.0',
      sampleRate: 100,
      premiumSampleRate: 100,
      trackFrustrations: true,
      trackInteractions: true,
      trackUserInteractions: true,
      allowedTracingUrls: [/^https:\/\/(?!frontapi)[a-zA-Z\d.-]+\.embluemail\.com\//],
    });
    datadogRum.startSessionReplayRecording && datadogRum.startSessionReplayRecording();
    if (userData && userData.email) {
      datadogRum.setUser({ id: userData.email, ...userData });
    }
  }, [userData, language, i18n, ready]);

  useEffect(() => {
    if (contactsEnabled) {
      document.body.classList.add('emblue-contacts-v2');
    } else {
      document.body.classList.remove('emblue-contacts-v2');
    }

    // eslint-disable-next-line
    window.addEventListener('message', ({ data }: any) => {
      if ((data as { payload: { event: string } }).payload?.event === configData.HOME.GO_TO_V2) {
        location.reload(); // Temporally to solve cache problems between v1 and v2
      } else if (
        (data as { payload: { event: string } }).payload?.event ===
        configData.HOME.CREATE_SENDER_MODAL
      ) {
        setShowSenderCreate(true);
      }
    });
  }, [contactsEnabled]);

  const mfaRequiredCookieFake = readCookie('mfaCookie');
  const skippedMfaCookie = readCookie('skippedMfa');

  useEffect(() => {
    const contactsCant = Number(localStorage.getItem('totalContacts') ?? 0);
    const blockEndpointByContacts =
      contactsCant === 0 || contactsCant >= configData.maxContactsToShowStatusCards;
    localStorage.setItem('showStatusCards', String(showStatusCardsFF && !blockEndpointByContacts));
  }, [showStatusCardsFF]);

  if (MFAIsEnabled && !mfaRequiredCookieFake && !skippedMfaCookie) {
    return (
      <BrowserRouter>
        <Routes>
          <Route element={<LoginLayout />} path={AUTH_PATHS.LOGIN_PATH}>
            <Route element={<MultiFactorAuthentication />} path={AUTH_PATHS.MFA} />
          </Route>
          <Route
            element={<Navigate to={`${AUTH_PATHS.LOGIN_PATH}/${AUTH_PATHS.MFA}`} />}
            path="/v2/*"
          />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <div>
      <div
        aria-hidden
        className="_app__container"
        style={{ position: 'relative', zIndex: 999 }}
        onClick={closeDropDown}
      >
        <CreateSenderModal isOpen={showSenderCreate} onClose={setShowSenderCreate} />
        <Spinner />
        <Zendesk withIcon={false} />
        <I18nextProvider i18n={i18n}>
          <FileProvider>
            {onsiteEnabled === true && (
              <BrowserRouter>
                <Routes>
                  <Route element={<OnsiteScreen />} path="/*" />
                </Routes>
              </BrowserRouter>
            )}
            {onsiteEnabled === false && (
              <>
                {version === 'v1' && (
                  <HashRouter>
                    <Routes>
                      <Route
                        element={
                          <MainContainer counter={freeTrialBlockState} userData={userData} />
                        }
                        path="/"
                      />
                      <Route
                        element={
                          <DashboardContainer counter={freeTrialBlockState} userData={userData} />
                        }
                        path="/dashboard"
                      />
                    </Routes>
                  </HashRouter>
                )}
                {version === 'v2' && (
                  <UserProvider userData={userData}>
                    <AppRouterVersionTwo
                      contactsEnabled={contactsEnabled}
                      freeTrialBlockState={freeTrialBlockState}
                    />
                  </UserProvider>
                )}
              </>
            )}
          </FileProvider>

          <DownloadContacts />
        </I18nextProvider>
      </div>
      {paymentInfo && <BlockAndSuspendModal paymentInfo={paymentInfo} userData={userData} />}
      {showBlockModalFreeTrialDemo && freeTrialBlockState?.isBlocked && (
        <FreeTrialDemo
          setBlockModal={setFreeTrialBlockState}
          showExtendsDemoButton={freeTrialBlockState.showExtendsDemoButton}
        />
      )}
    </div>
  );
};

export const App = () => {
  const env = getEnv();
  const [emblueLegacy, setEmblueLegacy] = useState({ contacts: false });
  const config = {
    url:
      env === 'production'
        ? 'https://unleash.embluemail.com/proxy'
        : 'https://unleash-staging.embluemail.com/proxy',
    clientKey: 'unleash-proxy-prod',
    refreshInterval: 15,
    appName: 'v2-frontend',
    environment: env === 'production' ? 'production' : 'development',
  };

  return (
    <FlagProvider config={config}>
      <EmblueLegacyContext.Provider value={{ ...emblueLegacy, setContext: setEmblueLegacy }}>
        <AppInstance />
      </EmblueLegacyContext.Provider>
    </FlagProvider>
  );
};
